import type { JSX } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { BotState } from '@apiary/shared'
import { botStateTone } from '@/lib/format'
import { usePrefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/cn'
import { toneColor } from './chart-tokens'

/**
 * Состав флота по состояниям: одна горизонтальная стопка + легенда.
 *
 * Почему не «пончик»: состояний у бота одиннадцать, а круговая диаграмма
 * перестаёт читаться после пяти секторов. Стопка остаётся честной при любом
 * числе категорий и даёт прямые подписи в легенде.
 *
 * Серия здесь и есть состояние, поэтому цвета берутся из статусной палитры, а
 * не из категориальной. Цвет — не единственный носитель смысла: в легенде
 * стоит название состояния и число, между сегментами — зазор.
 */

export interface StateBreakdownItem {
  state: BotState
  count: number
}

export interface StateBreakdownProps {
  items: StateBreakdownItem[]
  /** Клик по легенде — например, переход в список с фильтром. */
  onPick?: (state: BotState) => void
  className?: string
}

export function StateBreakdown({ items, onPick, className }: StateBreakdownProps): JSX.Element {
  const { t } = useTranslation()
  const reduced = usePrefersReducedMotion()

  const visible = useMemo(
    () =>
      items
        .filter(item => item.count > 0)
        .map(item => ({ ...item, color: toneColor(botStateTone(item.state)) })),
    [items]
  )
  const total = visible.reduce((sum, item) => sum + item.count, 0)

  if (total === 0) {
    return <p className={cn('text-xs text-ink-muted', className)}>{t('charts.noBots')}</p>
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* gap-0.5 = зазор поверхности между сегментами (иначе соседние состояния
          сливаются в одну полосу). */}
      <div
        role="img"
        aria-label={visible
          .map(item => `${t(`bot.state.${item.state}`)}: ${item.count}`)
          .join(', ')}
        className="flex h-3 gap-0.5 overflow-hidden rounded-full bg-panel-3"
      >
        {visible.map((item, index) => (
          <span
            key={item.state}
            className="first:rounded-l-full last:rounded-r-full"
            style={{
              width: `${(item.count / total) * 100}%`,
              backgroundColor: item.color,
              ...(reduced
                ? {}
                : {
                    transformOrigin: 'left',
                    animation: 'apiary-grow-x var(--duration-chart) var(--ease-spring) both',
                    animationDelay: `${index * 60}ms`
                  })
            }}
          />
        ))}
      </div>

      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        {visible.map(item => {
          const label = t(`bot.state.${item.state}`)
          const content = (
            <>
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-ink-muted">{label}</span>
              <span className="font-mono font-medium text-ink tabular-nums">{item.count}</span>
            </>
          )
          return (
            <li key={item.state}>
              {onPick === undefined ? (
                <span className="flex items-center gap-2 text-xs">{content}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => onPick(item.state)}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-1 py-0.5 text-xs transition-colors duration-150 hover:bg-panel-3"
                  aria-label={t('charts.filterByState', { state: label })}
                >
                  {content}
                </button>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
